import { z } from "zod";
import { IsoDate, Money, Timestamp } from "./primitives.js";
/**
 * @fileoverview Share prices, and the market capitalisation computed from them.
 *
 * A price is the only market data Mosaic consumes. Market cap is never taken from a
 * provider: it is computed from a price and the share count filed with EDGAR, so it has
 * one definition whichever provider answered, and each half carries its own date.
 */
/** The price providers a quote can come from. */
export const PriceSource = z.enum(["finnhub", "yahoo"]);
/** One share price for a ticker, as a price provider reported it. */
export const Quote = z.object({
    ticker: z.string(),
    price: Money,
    /**
     * The trading day the price is for, as the provider reports it — not the moment it was
     * fetched.
     */
    asOf: IsoDate,
    source: PriceSource,
    fetchedAt: Timestamp,
});
/**
 * A market capitalisation, with both of its halves dated.
 *
 * `sharesAsOf` is often months older than `priceAsOf`: the share count comes from the
 * last periodic report.
 */
export const MarketCap = z.object({
    ticker: z.string(),
    value: Money,
    price: Money,
    priceAsOf: IsoDate,
    shares: z.string().regex(/^\d+(\.\d+)?$/),
    sharesAsOf: IsoDate,
    source: PriceSource,
    /**
     * True when the share count predates the price by more than
     * `SHARES_STALE_AFTER_DAYS`, so a periodic report that was due has not refreshed it.
     */
    sharesStale: z.boolean(),
});
/**
 * A quarter plus the filing window: a share count older than this means a report that
 * should have refreshed it has not arrived or has not been ingested.
 */
export const SHARES_STALE_AFTER_DAYS = 95;
/**
 * Why the market-cap endpoint could not produce one: no price provider is configured,
 * the company has no ticker, no price came back, or no share count has been fetched.
 * Each wants a different response from a reader.
 */
export const MarketCapUnavailable = z.enum([
    "not_configured",
    "no_ticker",
    "no_price",
    "no_shares",
]);
/** A market capitalisation, or the reason one could not be computed. */
export const MarketCapResult = z.union([
    z.object({ available: z.literal(true), marketCap: MarketCap }),
    z.object({ available: z.literal(false), reason: MarketCapUnavailable }),
]);
//# sourceMappingURL=price.js.map